import { BaseService } from '@/infrastructure/api/base.service'
import type {
  CreditsSummaryResponse,
  UserCreditDetailResponse,
  SaveCreditsPayload,
  ApiCreditOverview,
  ApiUsageHistoryItem,
  CurrencyPricing,
  PurchaseCreditCommand,
  CheckoutSessionResult,
} from '../types/user-credits.types'

export class UserCreditsService extends BaseService {
  constructor() {
    super('')
  }

  async getSummary(): Promise<CreditsSummaryResponse> {
    return this.get<CreditsSummaryResponse>('/V2/UserCreditsSettings/Summary')
  }

  async getDetails(
    page: number,
    pageSize: number,
    sortBy: string,
    sortDesc: boolean,
  ): Promise<UserCreditDetailResponse[]> {
    return this.get<UserCreditDetailResponse[]>('/V2/UserCreditsSettings/Details', {
      params: { page, pageSize, sortBy, sortDesc },
    })
  }

  async saveCredits(payload: SaveCreditsPayload): Promise<{ result: boolean }> {
    return this.post<{ result: boolean }>('/V2/AccountManagement/SaveChanges', payload)
  }

  async getCreditOverview(): Promise<any> {
    const response = await this.get<{ result: boolean; companyManagement: any }>(
      '/V2/AccountManagement/GetCreditOverview',
    )
    return response.companyManagement
  }

  async getApiCreditOverview(): Promise<ApiCreditOverview> {
    // The backend (Newtonsoft, default resolver) returns the model in PascalCase, so map
    // it to the camelCase shape the UI uses — otherwise every field reads undefined and
    // the cards show 0 (Vue 2 parity: V2 binds the PascalCase fields directly).
    const response = await this.get<{
      result: boolean
      apiManagement: {
        TotalLeadCredits?: number
        TotalContactCredits?: number
        RemainingLeadCredits?: number
        RemainingContactCredits?: number
      }
    }>('/V2/AccountManagement/GetApiCreditOverview')

    const m = response.apiManagement ?? {}
    return {
      totalLeadCredits: m.TotalLeadCredits ?? 0,
      totalContactCredits: m.TotalContactCredits ?? 0,
      remainingLeadCredits: m.RemainingLeadCredits ?? 0,
      remainingContactCredits: m.RemainingContactCredits ?? 0,
    }
  }

  async getApiUsageHistory(
    startDate: string,
    endDate: string,
  ): Promise<{
    apiUsage: ApiUsageHistoryItem[]
    totalCompanyCreditsUsed: number
    totalContactCreditsUsed: number
  }> {
    // The wrapper keys are camelCase but each apiUsage row is a PascalCase backend model
    // (Newtonsoft default resolver) — map every row to the camelCase shape the table binds,
    // otherwise the rows render but every cell is empty.
    const response = await this.post<{
      apiUsage?: Array<{
        User?: string
        Date?: string
        RequestType?: string
        Companies?: number
        Contacts?: number
        Response?: string
        IpAddress?: string
        CreditsUsed?: number
      }>
      totalCompanyCreditsUsed?: number
      totalContactCreditsUsed?: number
    }>('/V2/AccountManagement/GetApiUsageHistory', {
      StartDate: startDate,
      EndDate: endDate,
    })

    return {
      apiUsage: (response.apiUsage ?? []).map((i) => ({
        user: i.User ?? '',
        date: i.Date ?? '',
        requestType: i.RequestType ?? '',
        companies: i.Companies ?? 0,
        contacts: i.Contacts ?? 0,
        response: i.Response ?? '',
        ipAddress: i.IpAddress ?? '',
        creditsUsed: i.CreditsUsed ?? 0,
      })),
      totalCompanyCreditsUsed: response.totalCompanyCreditsUsed ?? 0,
      totalContactCreditsUsed: response.totalContactCreditsUsed ?? 0,
    }
  }

  async downloadApiReport(startDate: string, endDate: string): Promise<Blob> {
    return this.post('/V2/AccountManagement/GetApiReport', {
      StartDate: startDate,
      EndDate: endDate,
    }, { responseType: 'blob' } as any)
  }

  async requestAdditionalCredits(): Promise<{ result: boolean }> {
    return this.get<{ result: boolean }>('/V2/AccountManagement/RequestAdditionalCredits')
  }

  async getPricePackages(): Promise<Record<string, CurrencyPricing>> {
    return this.get<Record<string, CurrencyPricing>>('/V2/PurchaseCredits/GetPricePackages')
  }

  async purchaseCredit(command: PurchaseCreditCommand): Promise<CheckoutSessionResult> {
    return this.post<CheckoutSessionResult>('/V2/PurchaseCredits/PurchaseCredit', command)
  }

  async getCheckoutSession(sessionId: string): Promise<CheckoutSessionResult> {
    return this.get<CheckoutSessionResult>('/V2/PurchaseCredits/GetCheckoutSession', {
      params: { checkoutSessionId: sessionId },
    })
  }
}
